/**
 * Puzzles from your own games: find what you missed.
 *
 * `yours-set.js` decides which moves become puzzles and when each comes back;
 * this page sets them on the board the other puzzle pages use (`play/table.js`).
 *
 * WHAT COUNTS AS RIGHT. The engine's move, or any move the engine rates within
 * a Slip of it (`CLOSE_ENOUGH`). Anything else is checked by the engine, here in
 * the browser, before it is refused — so "that is not it" is always a measured
 * answer, and a second good move is never called wrong. A move that is not right
 * is not played onto the board, as on the mate puzzles. If the engine cannot
 * run, only its own move counts, and the page says so rather than guessing.
 *
 * WHAT IS KEPT. Only how each puzzle went (`immortal-mistakes`, synced to the
 * account). A puzzle counts as found only if it was found without a wrong try;
 * a wrong try or "Show me" sends it back to tomorrow.
 *
 * FINDING MORE. A game becomes puzzles once it has been reviewed, which the
 * player does when a game is watched. This page can also review your games
 * itself, five at a time, with the same engine and the same saved reviews, so a
 * fresh import turns into puzzles without opening every game.
 */

import { createTable, escapeHtml, uciOf } from '../play/table.js';
import { Analyst } from '../play/analyst.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { mountCameraMenu, CAMERA_WORDS } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';
import { count } from '../analytics.js';
import { onMerged, read as readProgress, syncSoon, write as writeProgress } from '../progress.js';
import { files as readFiles, personName, records as readRecords } from '../data/imported.js';
import {
  allEvals, keyOf, positionsOf, runReview, winChance,
} from '../watch/review.js';
import { dayNumber } from '../daily/day.js';
import {
  CLOSE_ENOUGH, MIN_PLIES, MISTAKES_KEY, complete, grade, mergeMistakes, puzzlesFrom, queueFor,
  sidesOf, standing,
} from './yours-set.js';

const $ = (id) => document.getElementById(id);

mountNav($('nav-slot'), { here: 'puzzles', base: '../' });
mountInstall($('install-slot'), { base: '../' });

/** How many games one press of "Find puzzles" reviews. */
const BATCH = 5;

// `?day=` moves the calendar, for `scripts/check-yours.mjs`; it changes nothing
// but what this browser shows as due.
const today = Number(new URLSearchParams(location.search).get('day')) || dayNumber();

// ------------------------------------------------------------------ progress

function readState() {
  try {
    const raw = JSON.parse(readProgress(MISTAKES_KEY) || '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  } catch { return {}; }
}

let state = readState();

function saveState() {
  state = mergeMistakes(state, {});
  writeProgress(MISTAKES_KEY, JSON.stringify(state));
}

// ------------------------------------------------------------ your games

/** `{ id, moves, white, black, date, sides, mine, key }`, yours first, newest file first. */
let games = [];
let evalsByKey = new Map();
let puzzles = [];

const dateOf = (h) => String(h?.UTCDate || h?.Date || '').replace(/\.?\?\?/g, '').replace(/\./g, '-');

async function gather() {
  const [recs, fileList, evals] = await Promise.all([readRecords(), readFiles(), allEvals()]);
  const sides = sidesOf(recs, fileList);
  const added = new Map(fileList.map((f) => [f.id, f.addedAt || 0]));
  games = recs
    .filter((r) => Array.isArray(r.moves) && r.moves.length >= MIN_PLIES)
    .map((r) => ({
      id: r.id,
      moves: r.moves,
      white: personName(r.headers?.White),
      black: personName(r.headers?.Black),
      date: dateOf(r.headers),
      sides: sides.get(r.id) || ['w', 'b'],
      key: keyOf(r.moves),
      added: added.get(r.fileId) || 0,
      n: r.n || 0,
    }))
    .map((g) => ({ ...g, mine: g.sides.length === 1 }))
    .sort((a, b) => (b.mine - a.mine) || (b.added - a.added) || (a.n - b.n));
  evalsByKey = evals;
  rebuild();
}

function rebuild() {
  const seen = new Set();
  puzzles = [];
  for (const g of games) {
    const evals = evalsByKey.get(g.key);
    if (!evals?.length) continue;
    for (const p of puzzlesFrom(g, evals, g.sides)) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      puzzles.push({ ...p, mine: g.mine });
    }
  }
}

/** Whether a game's review is finished. The cheap count first; the exact check only near the end. */
function reviewed(g) {
  const evals = evalsByKey.get(g.key);
  if (!evals || evals.filter(Boolean).length < g.moves.length - 1) return false;
  return complete(positionsOf(g.moves), evals);
}

const unreviewed = () => {
  const mine = games.filter((g) => g.mine && !reviewed(g));
  return mine.length ? mine : games.filter((g) => !reviewed(g));
};

// ---------------------------------------------------------------- the engine

let analyst = null;
let engineFailed = false;

async function engine() {
  if (engineFailed) return null;
  analyst ??= new Analyst();
  try {
    await analyst.start();
    return analyst;
  } catch {
    engineFailed = true;
    return null;
  }
}

/**
 * The engine's word on a tried move: true if it is as good as the answer, the
 * number of points it is worse by if not, or null if the engine cannot say.
 */
async function measure(p, uci) {
  const a = await engine();
  if (!a) return null;
  try {
    const best = await a.evaluate([...p.before, p.best]);
    const tried = await a.evaluate([...p.before, uci]);
    // Both are scored for the side to move AFTER the move: the opponent. Negated.
    const lost = winChance(-best.cp) - winChance(-tried.cp);
    return lost < CLOSE_ENOUGH ? true : Math.round(lost);
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------ the board

const table = await createTable();
// The six angles as one button over the board (`../camera-menu.js`).
mountCameraMenu($('views'), { labels: CAMERA_WORDS });
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
const live = table.live;

/** solving | found | shown | empty */
let phase = 'empty';
let busy = false;
let queue = [];
let current = null;
/** Has this showing been marked yet? Only the first answer counts. */
let marked = false;
let doneToday = 0;

table.canPick = () => phase === 'solving' && !busy;
table.onPick = (mv) => { judge(mv); };

const sideWord = (side) => (side === 'w' ? 'White' : 'Black');
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function mark(found) {
  if (marked || !current) return;
  marked = true;
  state[current.id] = grade(state[current.id], found, today);
  saveState();
  doneToday++;
  drawStanding();
}

async function nextPuzzle() {
  if (busy) return;
  current = queue.shift() || null;
  marked = false;
  $('verdict').hidden = true;
  if (!current) return showEmpty();
  phase = 'solving';
  $('empty').hidden = true;
  $('ask').hidden = false;
  $('show').disabled = false;
  $('next').disabled = false;
  const p = current;
  table.you = p.side;
  table.opponentName = p.side === 'w' ? p.black : p.white;
  table.state = 'playing';
  table.explainMate(false);
  await table.setScore(p.before);
  await table.faceYourSide(true);
  const them = p.side === 'w' ? p.black : p.white;
  const when = p.date ? `, ${p.date}` : '';
  // A file with nobody in most of its games does not say which side is you.
  $('ask-line').innerHTML = p.mine
    ? `<b>Move ${p.moveNo}, you had ${sideWord(p.side)}.</b> You played ${escapeHtml(p.playedSan)} here. Find a better move.`
    : `<b>Move ${p.moveNo}, ${sideWord(p.side)} to play.</b> ${escapeHtml(p.playedSan)} was played here. Find a better move.`;
  $('ask-game').textContent = p.mine ? `Your game against ${them}${when}` : `${p.white} vs ${p.black}${when}`;
  table.setPills([p.word, `Cost ${p.drop} points`, state[p.id] ? 'Again' : 'New']);
  table.say('');
  table.setLead(`${sideWord(p.side)} to play. What did you miss?`);
  table.setTag('');
  drawStanding();
  // Warm the engine while the position is being looked at.
  engine();
}

async function judge(mv) {
  if (phase !== 'solving' || busy) return;
  const p = current;
  busy = true;
  try {
    const pos = live.pos;
    const san = pos.san(mv);
    const uci = uciOf(mv);
    if (uci === p.best) return await found(mv, true);
    if (uci === p.played) return refuse(`${san} is what you played — it cost ${p.drop} points. Look for something better.`);
    table.say(`Checking ${san}…`);
    table.setTag('thinking');
    const verdict = await measure(p, uci);
    if (current !== p || phase !== 'solving') return;
    if (verdict === true) return await found(mv, false);
    if (verdict === null) return refuse(`${san} is not the move the engine found. The engine could not load here to check it further, so only its move counts.`);
    return refuse(`${san} is not it: it gives away ${verdict} points more than the best move.`);
  } finally {
    busy = false;
  }
}

function refuse(text) {
  mark(false);
  // `say` holds until the next thing is said, which is what a refusal wants.
  table.say(text);
  table.setTag('not it');
  setTimeout(() => { if (phase === 'solving') table.setTag(''); }, 1400);
}

async function found(mv, exact) {
  const p = current;
  const first = !marked;
  mark(true);
  phase = 'found';
  await table.apply(mv);
  table.state = 'over';
  table.setTag('');
  table.showMoves();
  table.say('');
  table.setLead(first ? 'Found it.' : 'Found it, this time.');
  count('mine-found', first ? 'first' : 'again');
  verdict(
    exact ? (first ? 'Found it — first time.' : 'Found it.')
      : `That works too. The engine's move was ${p.bestSan}.`,
  );
}

async function show() {
  if (phase !== 'solving' || busy) return;
  const p = current;
  mark(false);
  phase = 'shown';
  const mv = live.fromUci(p.best);
  if (mv) await table.apply(mv);
  table.state = 'over';
  table.setTag('');
  table.showMoves();
  table.say('');
  table.setLead(`The move was ${p.bestSan}.`);
  count('mine-shown');
  verdict(`The move was ${p.bestSan}. It comes back tomorrow.`);
}

/** Once it is over: what the move was, what went wrong in the game, and how hard it was. */
function verdict(headline) {
  const p = current;
  $('verdict-line').textContent = headline;
  $('verdict-why').textContent = `In the game, ${p.playedSan} was a ${p.word.toLowerCase()}. ${p.reason}`;
  $('verdict-hard').hidden = !p.hard;
  $('verdict-hard').textContent = p.hard ? `How hard ${p.bestSan} was to find — ${p.hard}` : '';
  const watch = `../watch/player.html?game=${encodeURIComponent(p.gameId)}&ply=${p.k}`;
  $('verdict-acts').innerHTML = `<a class="ctl" href="${escapeHtml(watch)}">See it in your game</a>`
    + `<button class="ctl primary" type="button" data-next="1">${queue.length ? 'Next puzzle' : 'Done'}</button>`;
  $('verdict').hidden = false;
  $('show').disabled = true;
  drawStanding();
}

// ------------------------------------------------------------ the panel

function drawStanding() {
  const s = standing(puzzles, state, today);
  const stats = [];
  if (queue.length + (phase === 'solving' ? 1 : 0)) stats.push(['left today', queue.length + (phase === 'solving' ? 1 : 0)]);
  if (s.learning) stats.push(['learning', s.learning]);
  if (s.done) stats.push(['learned', s.done]);
  if (s.fresh) stats.push(['not tried yet', s.fresh]);
  $('standing').innerHTML = puzzles.length
    ? stats.map(([word, n]) => `<span class="y-stat"><b>${n}</b> ${word}</span>`).join('')
    : '';
  $('done').textContent = doneToday ? `${doneToday} today` : '';
  drawFind();
}

function drawFind() {
  const left = unreviewed();
  $('find').hidden = !games.length || (!left.length && !mining);
  if (mining) return;
  const n = Math.min(BATCH, left.length);
  $('find-say').textContent = left.length
    ? `${plural(left.length, left[0].mine ? 'of your games has' : 'game has', left[0].mine ? 'of your games have' : 'games have')} not been reviewed yet. Reviewing finds the moves that cost you. It runs here, in your browser.`
    : '';
  $('find-go').textContent = `Find puzzles in ${n === 1 ? 'one more game' : `${n} more games`}`;
  $('find-go').hidden = !left.length;
  $('find-stop').hidden = true;
  $('find-progress').hidden = true;
}

function showEmpty() {
  phase = 'empty';
  current = null;
  $('ask').hidden = true;
  $('empty').hidden = false;
  $('show').disabled = true;
  $('next').disabled = true;
  table.state = 'over';
  table.say('');
  table.setTag('');
  table.setPills([]);
  const s = standing(puzzles, state, today);
  const importLink = $('empty-import');
  importLink.hidden = true;
  if (!games.length) {
    $('empty-line').textContent = 'No games of yours here yet.';
    $('empty-say').textContent = 'Import your games from Lichess or Chess.com, or a PGN file. The moves that cost you become puzzles here.';
    importLink.hidden = false;
    table.setLead('Import your games to begin.');
  } else if (!puzzles.length) {
    $('empty-line').textContent = unreviewed().length ? 'Your games have not been reviewed yet.' : 'No mistakes to set.';
    $('empty-say').textContent = unreviewed().length
      ? 'Press "Find puzzles" below. Each game is reviewed by the engine, and every move that cost you 20 points or more becomes a puzzle.'
      : 'None of your reviewed games has a move that cost 20 points or more. Import more games to find some.';
    importLink.hidden = unreviewed().length > 0;
    importLink.textContent = 'Import more games';
    table.setLead('Nothing to set yet.');
  } else {
    const wait = s.nextDay == null ? '' : s.nextDay - today === 1 ? ' The next one is due tomorrow.' : ` The next one is due in ${s.nextDay - today} days.`;
    $('empty-line').textContent = doneToday ? 'That is all for today.' : 'Nothing due today.';
    $('empty-say').textContent = s.fresh
      ? `That is today's ten new puzzles; ${plural(s.fresh, 'more waits', 'more wait')} for tomorrow.${wait}`
      : `Every puzzle is either learned or waiting for its day.${wait}`;
    table.setLead(doneToday ? 'Done for today.' : 'Nothing due.');
  }
  drawStanding();
}

// ------------------------------------------------------------ finding more

let mining = null;

async function findMore() {
  if (mining) return;
  const todo = unreviewed().slice(0, BATCH);
  if (!todo.length) return;
  mining = new AbortController();
  const { signal } = mining;
  $('find-go').hidden = true;
  $('find-stop').hidden = false;
  $('find-progress').hidden = false;
  $('find-note').textContent = 'Starting the engine…';
  const a = await engine();
  if (!a) {
    mining = null;
    $('find-note').textContent = 'The engine could not start in this browser, so games cannot be reviewed here.';
    drawFind();
    return;
  }
  const before = puzzles.length;
  let done = 0;
  let failed = null;
  try {
    for (const g of todo) {
      if (signal.aborted) break;
      const them = g.sides.length === 1 ? (g.sides[0] === 'w' ? g.black : g.white) : `${g.white} vs ${g.black}`;
      const { evals } = await runReview(g.moves, a, {
        signal,
        onProgress: (_, have, total) => {
          $('find-note').textContent = `Game ${done + 1} of ${todo.length}: ${g.sides.length === 1 ? `against ${them}` : them}`;
          $('find-bar').style.width = `${Math.round((100 * (done + have / total)) / todo.length)}%`;
        },
      });
      evalsByKey.set(g.key, evals);
      if (!signal.aborted) done++;
    }
  } catch (err) {
    // The engine gave out, or the browser would not store the answers. The
    // games reviewed so far still give their puzzles, and the button comes back.
    failed = err;
  }
  count('mine-review', '', done);
  mining = null;
  rebuild();
  const added = puzzles.length - before;
  $('find-note').textContent = `${plural(done, 'game')} reviewed, ${plural(added, 'new puzzle')}.`
    + (failed ? ` The review stopped there: ${failed.message || failed}.` : '');
  refill();
  drawFind();
}

/** New puzzles join today's queue behind the ones already waiting. */
function refill() {
  const waiting = new Set(queue.map((p) => p.id));
  if (current) waiting.add(current.id);
  for (const p of queueFor(puzzles, state, today)) {
    if (!waiting.has(p.id)) queue.push(p);
  }
  if (phase === 'empty') nextPuzzle();
  else drawStanding();
}

// ------------------------------------------------------------------ wiring

$('next').addEventListener('click', () => {
  if (phase === 'solving') mark(false);
  nextPuzzle();
});
$('show').addEventListener('click', show);
$('verdict-acts').addEventListener('click', (event) => {
  if (event.target.closest('[data-next]')) nextPuzzle();
});
$('find-go').addEventListener('click', findMore);
$('find-stop').addEventListener('click', () => mining?.abort());
addEventListener('keydown', (event) => {
  if (event.metaKey || event.ctrlKey || event.altKey || event.target.matches('input, textarea')) return;
  const key = event.key.toLowerCase();
  if (key === 'n') { event.preventDefault(); $('next').click(); }
  if (key === 's') { event.preventDefault(); show(); }
});

onMerged(() => {
  const theirs = readState();
  state = mergeMistakes(state, theirs);
  drawStanding();
});
syncSoon();

// ------------------------------------------------------------------ and go

await gather();
queue = queueFor(puzzles, state, today);
count('mine-open', '', puzzles.length);
await nextPuzzle();

// What `scripts/check-yours.mjs` drives the page through.
window.__yours = {
  get phase() { return phase; },
  get busy() { return busy || Boolean(mining); },
  get current() { return current; },
  get queue() { return queue.map((p) => p.id); },
  get puzzles() { return puzzles; },
  get state() { return JSON.parse(JSON.stringify(state)); },
  play: (uci) => {
    const mv = live.fromUci(uci);
    if (!mv) throw new Error(`${uci} is not legal here`);
    return judge(mv);
  },
  show,
  next: nextPuzzle,
  findMore,
};
